"use client";

import React from "react";
import { twMerge } from "tailwind-merge";

import { HTMLMarkdown } from "@/components/markdown/HTMLMarkdown";
import { useDialog } from "@/hooks/use-dialog";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";
import { QuestionIcon } from "ui/icon/shared";

type Props = {
  title: string;
  variant: "h1" | "h2" | "h3" | "p";
  popoverTitle: string | JSX.Element;
  popoverContent: string | JSX.Element;
  iconClassName?: string;
};

export const PopoverTitle = React.memo<Props>(
  ({ title, variant, popoverTitle, popoverContent, iconClassName }) => {
    const { t } = useLang();
    const { Dialog, openDialog, closeDialog } = useDialog();
    const TitleTag = variant as keyof JSX.IntrinsicElements;
    return (
      <div className="flex items-center gap-2" id="popover-title">
        <TitleTag>{title}</TitleTag>
        <QuestionIcon
          className={twMerge(
            "mt-0.5 h-4 w-4 cursor-pointer fill-zinc-400 transition-colors hover:fill-sky-700",
            iconClassName
          )}
          onClick={openDialog}
          role="button"
          aria-label="open pop up"
        />
        <Dialog title={popoverTitle} width={812}>
          <div
            className="flex flex-col items-center gap-2 px-3 py-8 text-sm sm:px-14"
            aria-label="pop up info"
          >
            {typeof popoverContent === "string" ? (
              <HTMLMarkdown>{popoverContent}</HTMLMarkdown>
            ) : (
              popoverContent
            )}
            <Button
              className="mx-2 mt-4 w-full max-w-[280px]"
              onClick={closeDialog}
              aria-label="close pop up"
            >
              {t.analysis.shared.close}
            </Button>
          </div>
        </Dialog>
      </div>
    );
  }
);

PopoverTitle.displayName = "PopoverTitle";
